import React, { useRef, useEffect, useMemo, useCallback } from 'react'
import { Box } from '@mui/material'
import { AgGridReact } from 'ag-grid-react'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import { dateComparator, relativeTime, isDateLessThan } from 'components/common/AgGridUtils'
import useUnits, { UNITS_FOR } from 'components/common/hooks/useUnits'
import { numberWithCommasDecimals } from 'utils/stringFunctions'

const AlertHistoryDetailsGrid = ({ alertDetails }) => {
  const _isMounted = useRef(false)
  const gridApi = useRef(null)
  const { getAgGridTheme, theme } = useInnovaTheme()
  const { getUnitsText } = useUnits()

  const alertColors = {
    warning: 'orange',
    critical: 'red',
  }

  const getAlertColor = (level) => alertColors[level.toLowerCase()] || 'green'

  useEffect(() => {
    _isMounted.current = true
    return () => {
      _isMounted.current = false
    }
  }, [])

  const defaultColDef = useMemo(() => {
    return {
      resizable: true,
      sortable: false,
      autoHeight: true,
      editable: false,
      suppressHeaderMenuButton: false,
      headerClass: 'header-no-padding',
    }
  }, [])

  const onGridReady = (params) => {
    gridApi.current = params.api
  }

  const onFirstDataRendered = (params) => {
    if (gridApi.current) autoSizeColumns()
  }

  const autoSizeColumns = () => {
    if (gridApi.current === null) return
    if (gridApi.current.isDestroyed()) return
    gridApi.current?.autoSizeAllColumns()
  }

  const getRowId = useMemo(() => {
    return (params) => {
      return `${params.data?.alertName}-${params.data?.alertLevel}-${params.data?.alertType}-${params.data?.offsetWell}-${params.data?.depth}-${params.data?.timeStamp}`
    }
  }, [])

  const relativeTimeFormatter = useCallback(relativeTime, [])

  const dateFilterComparator = useCallback(dateComparator, [])

  const dateTimeFormatter = useCallback((value) => {
    if (!value || typeof value !== 'string') return ''
    if (isDateLessThan(value, '1990-01-01')) return ''

    return new Date(value).toLocaleString('default', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    })
  }, [])

  const leftAlignCell = () => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
  })

  const centerAlignCell = () => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  })

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'Alert Type',
        colId: 'alertType',
        field: 'alertType',
        cellStyle: leftAlignCell,
        pinned: 'left',
        lockPosition: 'left',
      },
      {
        headerName: 'Alert Level',
        colId: 'alertLevel',
        field: 'alertLevel',
        cellStyle: (params) => ({ ...leftAlignCell, backgroundColor: getAlertColor(params.value) }),
        pinned: 'left',
        lockPosition: 'left',
      },
      {
        headerName: 'Alert Name',
        colId: 'alertName',
        field: 'alertName',
        cellStyle: leftAlignCell,
        pinned: 'left',
        lockPosition: 'left',
      },
      {
        headerName: 'Offset Well',
        colId: 'offsetWell',
        field: 'offsetWell',
        cellStyle: leftAlignCell,
      },
      {
        headerName: 'Depth',
        colId: 'depth',
        field: 'depth',
        cellStyle: leftAlignCell,
        valueGetter: (params) => {
          if (!params.data?.depth) return ''
          if (params.data?.depth === '') return ''
          return numberWithCommasDecimals(params.data?.depth)
        },
      },
      {
        headerName: 'Unit',
        colId: 'alertUnit',
        field: 'alertUnit',
        cellStyle: centerAlignCell,
        valueGetter: (params) => {
          if (!params.data?.alertUnit) return ''
          if (params.data?.alertUnit === '') return ''
          return getUnitsText(UNITS_FOR[params.data?.alertUnit])
        },
      },
      {
        headerName: 'Value',
        colId: 'value',
        field: 'value',
        cellStyle: leftAlignCell,
        valueGetter: (params) => {
          if (!params.data?.value) return ''
          if (params.data?.value === '') return ''
          return numberWithCommasDecimals(params.data?.value, 2)
        },
      },
      {
        headerName: 'Notification Type',
        colId: 'notification',
        field: 'notification',
        cellStyle: leftAlignCell,
      },
      {
        headerName: 'Time Stamp',
        colId: 'timeStamp',
        field: 'timeStamp',
        cellStyle: leftAlignCell,
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: dateFilterComparator,
        },
        valueFormatter: (params) => {
          return `${dateTimeFormatter(params.value)} [${relativeTimeFormatter(params.value)}]`
        },
      },
    ],
    [getUnitsText, dateTimeFormatter, relativeTimeFormatter], // eslint-disable-line react-hooks/exhaustive-deps
  )

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
      }}>
      <div className={getAgGridTheme(theme)} style={{ width: '100%', height: '100%' }}>
        <AgGridReact
          rowData={alertDetails}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
          getRowId={getRowId}
          rowSelection={{
            mode: 'multiRow',
            checkboxes: false,
          }}
          cellSelection={'true'}
        />
      </div>
    </Box>
  )
}
export default AlertHistoryDetailsGrid
